import { useEffect, useState, useCallback, useRef } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Cpu, MemoryStick, HardDrive, Zap, AlertCircle, Users, Loader2, Pause, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Gauge({ label, value, max = 100, unit = '%', Icon }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = pct > 80 ? 'from-red-500 to-red-600' : pct > 60 ? 'from-amber-500 to-amber-600' : 'from-emerald-500 to-emerald-600';
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`gauge-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400 flex items-center gap-1.5"><Icon size={12} /> {label}</span>
        <span className="text-xs font-bold text-zinc-200">{Math.round(value * 10) / 10}{unit}</span>
      </div>
      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mt-2">
        <div className={`h-full bg-gradient-to-r ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function bytes(n) {
  if (!n) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function AdminSystemHealth() {
  const { authFetch } = useAdmin();
  const [tab, setTab] = useState('resources');
  const [resources, setResources] = useState(null);
  const [perf, setPerf] = useState(null);
  const [errs, setErrs] = useState(null);
  const [vis, setVis] = useState(null);
  const [live, setLive] = useState(true);
  const tmr = useRef(null);

  const load = useCallback(async () => {
    const endpoint = { resources: 'resources', performance: 'performance', errors: 'error-logs', visitors: 'visitor-load' }[tab];
    try {
      const r = await authFetch(`${API}/admin/system-health/${endpoint}`);
      const d = await r.json();
      if (tab === 'resources') setResources(d);
      if (tab === 'performance') setPerf(d);
      if (tab === 'errors') setErrs(d);
      if (tab === 'visitors') setVis(d);
    } catch (e) { console.error(e); }
  }, [authFetch, tab]);

  useEffect(() => {
    load();
    if (live) {
      tmr.current = setInterval(load, 5000);
      return () => clearInterval(tmr.current);
    }
  }, [load, live]);

  const tabs = [
    { k: 'resources', l: 'Resursi' },
    { k: 'performance', l: 'Performanse' },
    { k: 'errors', l: 'Greške' },
    { k: 'visitors', l: 'Posjetitelji' },
  ];

  return (
    <div className="p-6 max-w-7xl" data-testid="admin-system-health-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><Cpu size={28} className="text-red-500" /> Zdravlje sustava</h1>
          <p className="text-xs text-zinc-500 mt-1">Uživo mjerenja ({live ? 'auto 5s' : 'pauzirano'}).</p>
        </div>
        <button onClick={() => setLive(l => !l)} className="px-4 py-2 bg-zinc-900 border border-zinc-800 hover:border-red-500 text-zinc-300 text-sm rounded-md flex items-center gap-2" data-testid="live-toggle">
          {live ? <Pause size={14} /> : <Play size={14} />} {live ? 'Pauziraj' : 'Nastavi'}
        </button>
      </header>

      <div className="flex gap-1 mb-5 border-b border-zinc-800">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm border-b-2 ${tab === t.k ? 'border-red-500 text-zinc-100' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`} data-testid={`tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === 'resources' && (!resources ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Gauge label="CPU" value={resources.cpu.percent} Icon={Cpu} />
            <Gauge label="RAM" value={resources.memory.percent} Icon={MemoryStick} />
            <Gauge label="Swap" value={resources.swap.percent} Icon={MemoryStick} />
            <Gauge label="Disk" value={resources.disk.percent} Icon={HardDrive} />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500 mb-1">RAM</div>
              <div className="text-sm text-zinc-200">{bytes(resources.memory.used)} / {bytes(resources.memory.total)}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500 mb-1">Disk</div>
              <div className="text-sm text-zinc-200">{bytes(resources.disk.used)} / {bytes(resources.disk.total)} · slobodno {bytes(resources.disk.free)}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500 mb-1">Mreža</div>
              <div className="text-sm text-zinc-200">↑ {bytes(resources.network.bytes_sent)} ↓ {bytes(resources.network.bytes_recv)}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500 mb-1">CPU</div>
              <div className="text-sm text-zinc-200">{resources.cpu.count} cores · {resources.cpu.freq?.current ? Math.round(resources.cpu.freq.current) + ' MHz' : ''}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500 mb-1">Proces</div>
              <div className="text-sm text-zinc-200">RSS {bytes(resources.process.rss)} · niti {resources.process.threads}</div>
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500 mb-1">Uptime</div>
              <div className="text-sm text-zinc-200">{Math.floor(resources.uptime_seconds / 3600)}h {Math.floor((resources.uptime_seconds % 3600) / 60)}m</div>
            </div>
          </div>
        </div>
      ))}

      {tab === 'performance' && (!perf ? <Loader2 className="animate-spin" /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">RPM (1m)</div><div className="text-2xl font-bold text-zinc-100">{perf.rpm_1m}</div></div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">RPM (5m avg)</div><div className="text-2xl font-bold text-zinc-100">{perf.rpm_5m}</div></div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">Avg response</div><div className="text-2xl font-bold text-zinc-100">{perf.avg_response_ms}ms</div></div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">Greške 1h</div><div className="text-2xl font-bold text-red-400">{perf.error_count_1h}</div></div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Zap size={14} /> Top 10 najsporijih endpointa</h3>
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500 border-b border-zinc-800"><th className="text-left p-1.5">Path</th><th className="text-right p-1.5">Count</th><th className="text-right p-1.5">Avg</th><th className="text-right p-1.5">Max</th></tr></thead>
              <tbody>
                {(perf.top_slow_endpoints || []).map(e => (
                  <tr key={e.path} className="border-b border-zinc-900 last:border-0"><td className="p-1.5 truncate font-mono text-zinc-300">{e.path}</td><td className="text-right p-1.5 text-zinc-400">{e.count}</td><td className="text-right p-1.5 text-amber-400">{e.avg_ms}ms</td><td className="text-right p-1.5 text-red-400">{e.max_ms}ms</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {tab === 'errors' && (!errs ? <Loader2 className="animate-spin" /> : (
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><AlertCircle size={14} /> Runtime greške ({errs.runtime_errors.length})</h3>
            <div className="max-h-96 overflow-y-auto text-xs font-mono space-y-1">
              {errs.runtime_errors.length === 0 ? <p className="text-zinc-500">Nema grešaka</p> :
                errs.runtime_errors.map((e, i) => (
                  <div key={i} className="p-2 bg-zinc-950 rounded text-red-300">[{e.timestamp?.slice(11, 19)}] {e.path || '-'} · {e.error?.slice(0, 200) || e.status}</div>
                ))}
            </div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <h3 className="text-sm font-semibold mb-3">backend.err.log (zadnjih 80 linija)</h3>
            <pre className="text-[10px] text-zinc-400 bg-zinc-950 p-3 rounded max-h-80 overflow-auto whitespace-pre-wrap">{(errs.log_tail || []).join('\n')}</pre>
          </div>
        </div>
      ))}

      {tab === 'visitors' && (!vis ? <Loader2 className="animate-spin" /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">5 min</div><div className="text-2xl font-bold">{vis.events_5m}</div></div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">15 min</div><div className="text-2xl font-bold">{vis.events_15m}</div></div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">1 sat</div><div className="text-2xl font-bold">{vis.events_1h}</div></div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"><div className="text-xs text-zinc-500">24 sata</div><div className="text-2xl font-bold">{vis.events_24h}</div></div>
          </div>
          <div className={`rounded-xl p-6 text-center font-bold text-lg ${vis.load_status === 'high' ? 'bg-red-500/10 text-red-400 border border-red-500/30' : vis.load_status === 'medium' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'}`}>
            <Users size={24} className="inline mr-2" />
            Opterećenje: {vis.load_status.toUpperCase()} (CPU {vis.cpu_percent}% · RAM {vis.memory_percent}%)
          </div>
        </div>
      ))}
    </div>
  );
}
